import { c as _c } from "react/compiler-runtime";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { ACTION_BTN_SIZE, ACTION_ICON_SIZE } from "@styles/common-styles";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { useSharedMarkupActions } from "@hooks/project-markups/use-shared-markups-actions";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { OPEN_PROJECT_TARGET_ATTRIBUTE, getOpenProjectTargetName, trackOpenProject } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { Button, HolobuilderIcon, ViewerIcon, Tooltip } from "@flat-ui";
/**
 * Component that represents an action button to open a markup in Sphere Viewer or HoloBuilder.
 * Renders an icon button that, when clicked, opens the markup in the specified viewer/editor.
 *
 * @param markup The properties of the markup to be opened
 * @returns A JSX element representing the action button for opening the markup,
 * wrapped in a tooltip component with a dynamic title based on the workspace type.
 */
export function MarkupActionSphereViewer(t0) {
  const $ = _c(36);
  const {
    markup,
    isLabeled: t1,
    viewerHref
  } = t0;
  const isLabeled = t1 === undefined ? false : t1;
  const {
    trackAsyncEvent
  } = useTrackEvent();
  let t2;
  if ($[0] !== markup) {
    t2 = {
      markup
    };
    $[0] = markup;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    openTarget,
    markupHref
  } = useSharedMarkupActions(t2);
  const href = openTarget === ProjectLaunchTarget.sphereViewer ? viewerHref ?? markupHref : markupHref;
  const selectedProject = useAppSelector(selectedProjectSelector);
  const projectLaunchSettings = useAppSelector(defaultProjectLaunchTargetSelector);
  let t3;
  bb0: {
    if (!projectLaunchSettings.canOpenWebEditor) {
      t3 = "Open annotation";
      break bb0;
    }
    let t4;
    if ($[2] !== projectLaunchSettings.defaultOpenProjectTarget) {
      t4 = getOpenProjectTargetName(projectLaunchSettings.defaultOpenProjectTarget);
      $[2] = projectLaunchSettings.defaultOpenProjectTarget;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    t3 = `Open annotation in ${t4}`;
  }
  const openAnnotationTitle = t3;
  let t4;
  if ($[4] !== openTarget || $[5] !== selectedProject || $[6] !== trackAsyncEvent) {
    t4 = function onOpenMarkup(event, clickType) {
      event.stopPropagation();
      trackOpenProject({
        openFrom: "projectDetails-annotations",
        clickType,
        numberOfMembers: selectedProject?.members.length,
        openTarget,
        ...selectedProject,
        trackAsyncEvent
      });
    };
    $[4] = openTarget;
    $[5] = selectedProject;
    $[6] = trackAsyncEvent;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onOpenMarkup = t4;
  if (!markupHref) {
    return null;
  }
  const Icon = openTarget === ProjectLaunchTarget.sphereViewer ? ViewerIcon : HolobuilderIcon;
  if (isLabeled) {
    let t5;
    if ($[8] !== Icon) {
      t5 = <Icon />;
      $[8] = Icon;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    let t7;
    let t8;
    if ($[10] !== onOpenMarkup) {
      t6 = event_0 => onOpenMarkup(event_0, "left button click");
      t7 = event_1 => onOpenMarkup(event_1, "middle button click");
      t8 = event_2 => onOpenMarkup(event_2, "open context menu");
      $[10] = onOpenMarkup;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
    } else {
      t6 = $[11];
      t7 = $[12];
      t8 = $[13];
    }
    let t9;
    if ($[14] !== href || $[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
      t9 = <Button size="s" icon={t5} href={href} onClick={t6} onAuxClick={t7} onContextMenu={t8} data-testid="markup-open-annotation-button">Open</Button>;
      $[14] = href;
      $[15] = t5;
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== openAnnotationTitle || $[21] !== t9) {
      t10 = <Tooltip component="div" title={openAnnotationTitle}>{t9}</Tooltip>;
      $[20] = openAnnotationTitle;
      $[21] = t9;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  let t5;
  let t6;
  let t7;
  if ($[23] !== onOpenMarkup) {
    t5 = event_3 => onOpenMarkup(event_3, "left button click");
    t6 = event_4 => onOpenMarkup(event_4, "middle button click");
    t7 = event_5 => onOpenMarkup(event_5, "open context menu");
    $[23] = onOpenMarkup;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
  }
  let t8;
  if ($[27] !== Icon || $[28] !== href || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t8 = <SphereIconButton buttonSize={ACTION_BTN_SIZE} iconSize={ACTION_ICON_SIZE} component={Icon} target={OPEN_PROJECT_TARGET_ATTRIBUTE} href={href} onClick={t5} onAuxClick={t6} onContextMenu={t7} />;
    $[27] = Icon;
    $[28] = href;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== openAnnotationTitle || $[34] !== t8) {
    t9 = <Tooltip component="div" title={openAnnotationTitle}>{t8}</Tooltip>;
    $[33] = openAnnotationTitle;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}